/**
 * 작성자 : 홍선영
 * 날짜 : 2023.12.18
 * 기능 : 방송장비테스트 모달
 */
/*
 * 수정자 : 김광민
 * 수정 날짜 : 2024.03.05
 * 수정 이유 1 : global style 걷어내고 컴포넌트 내 스타일시트 새로 작성
 * 수정 이유 2 : html 구조 재구성 및 전체 스타일시트 수정
 * 수정 이유 3 : map 메서드로 감싸는 부분 별도 컴포넌트로 추출
 */

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import CheckBoxCircle from './button/CheckBoxCircle';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { apiPatch } from '../services/_common';

const Root = styled.div`
  height: 100%;
  width: 20rem;
  max-height: inherit;
  overflow: auto;
  display: flex;
  flex-direction: column;
  flex: 0 0 40%;
  background-color: var(--board);
  position: relative;

  input[type='range'] {
    padding: 0;
    cursor: pointer;
  }

  .header,
  .body > .list {
    background-color: var(--board);
    border-bottom: 1px solid var(--outline);
    padding: 0 0.5rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    gap: 0.5rem;

    .seperator {
      /* height: 1rem;
      border-left: 1px solid var(--outline-em); */
      &::before {
        content: '•';
      }
    }
  }
  .header {
    position: sticky;
    top: 0;
    height: 3rem;
    color: var(--text-tertiary);
    justify-content: space-between;
    .title {
      display: flex;
      gap: 0 0.25rem;
      align-items: center;
    }
  }
  .body {
    overflow: auto;
    .list {
      background-color: var(--board);
      border-bottom: 1px solid var(--outline);
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 0.875rem;
      padding: 0.5rem;
      flex-direction: column;

      .content_wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        &:hover.selectable {
          cursor: pointer;
          background-color: var(--tonal);
          .content > .index {
            background-color: var(--tonal-deep);
          }
        }

        &:active.selectable {
          cursor: pointer;
          background-color: var(--tonal-deep);
          .content > .index {
            background-color: var(--tonal);
          }
        }
      }

      .content {
        display: flex;
        align-items: center;
        flex-grow: 1;
        gap: 0.5rem;
        flex-shrink: 0;

        .text-container {
          display: flex;
          flex-grow: 1;
          gap: 0.25rem;
          flex-shrink: 0;
          align-items: center;

          > :first-child {
            margin: 0 0.5rem;
          }
        }

        .index {
          display: flex;
          justify-content: center;
          align-items: center;
          min-width: 1.5rem;
          height: 1.5rem;
          background-color: var(--tonal);
          color: var(--text-tertiary);
          border-radius: 0.25rem;
        }

        .text {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: var(--text-tertiary);
        }

        .badge {
          flex-shrink: 0;
          width: fit-content;
          padding: 0 0.75rem;
          display: flex;
          min-height: 2rem;
          align-items: center;
          gap: 0.375rem;
          color: var(--text-tertiary);
          background-color: var(--tonal);
          border-radius: 0.25rem;
          .bullet {
            display: flex;
            width: 0.5rem;
            height: 0.5rem;
            border-radius: 1rem;
          }
          &.black {
            background-color: var(--tonal-slate);
            color: var(--filled-slate-deep);
            .bullet {
              background-color: var(--filled-slate);
            }
          }
          &.green {
            background-color: var(--tonal-green);
            color: var(--filled-green-deep);
            .bullet {
              background-color: var(--filled-green);
            }
          }
          &.blue {
            background-color: var(--tonal-blue);
            color: var(--filled-blue-deep);
            .bullet {
              background-color: var(--primary);
            }
          }
          &.red {
            background-color: var(--tonal-red);
            color: var(--filled-red-deep);
            .bullet {
              background-color: var(--filled-red);
            }
          }
        }
      }

      .slider-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;

        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
          font-size: 1rem;
          color: #888;
          width: 28px;
          text-align: center;
        }

        input[type='range'] {
          flex: 1;
          height: 6px;
        }

        .volume-value {
          min-width: 1.5rem;
          text-align: right;
          color: var(--text-tertiary);
        }
      }
    }
  }
`;

type BroadcastList = {
  hCd: string;
  sCd: string;
  bsCd: string;
  bsId: string;
  bsIp: string;
  bsName: string;
  bsPwd: string;
  cName: null | string;
  pPort: string;
  tadCd: string;
  tadName: string;
  tatCd: string;
  tatGubun: string;
  volume: number;
};

const BroadcastEquipList = ({
  data,
  state,
  setState,
  isTunnel,
  selectSpeaker,
  setSelectSpeaker,
  isLiveBroadcast,
  isLoading,
}: {
  data: BroadcastList[];
  state: { index: number; connectValue: number; checked?: boolean }[];
  setState: Dispatch<SetStateAction<any>>;
  isTunnel?: boolean;
  selectSpeaker: number[];
  setSelectSpeaker: Dispatch<SetStateAction<number[]>>;
  isLiveBroadcast?: boolean;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const [checkAll, setCheckAll] = useState(false);
  const [settingsData, setSettingsData] = useState<number[]>(data.map((el: any) => el.volume));

  useEffect(() => {
    setSettingsData(data.map((el: any) => el.volume));
  }, [data]);

  const getColor = (params: any) => {
    if (params === 0 || !params) return 'black';
    if (params === 1) return 'green';
    if (params === 2) return 'blue';
    return 'red';
  };

  const getText = (params: any) => {
    if (params === 0 || !params) return t('연결중');
    if (params === 1) return t('방송가능');
    if (params === 2) return t('방송중');
    return t('방송불가');
  };

  useEffect(() => {
    const allChecked = state
      .filter((el: { connectValue: number }) => el.connectValue === 1 || el.connectValue === 2)
      .every((el: { connectValue: number; index: number }) => selectSpeaker.includes(el.index));
    setCheckAll(allChecked);
  }, [state, selectSpeaker]);

  const handleClickCheckAll = () => {
    if (isLiveBroadcast) return;
    if (isLoading) return;
    if (checkAll) {
      setSelectSpeaker([]);
      setCheckAll(false);
    } else {
      const newSelectSpeaker: number[] = [];
      state.forEach((el, index) => {
        if (el.connectValue === 1 || el.connectValue === 2) {
          newSelectSpeaker.push(index);
        }
      });
      setSelectSpeaker(newSelectSpeaker);
      setCheckAll(true);
    }
  };

  const handleClickCheckBox = (i: number) => {
    if (isLiveBroadcast) return;
    if (isLoading) return;
    if (state[i].connectValue === 1 || state[i].connectValue === 2) {
      if (selectSpeaker.includes(i)) {
        setSelectSpeaker((prev) => prev.filter((el) => el !== i));
      } else {
        setSelectSpeaker((prev) => [...prev, i]);
      }
    }
  };

  const sliderTimeout = useRef<NodeJS.Timeout | null>(null);

  // 슬라이더 값 처리
  const handleSliderValue = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setSettingsData((prev) => {
      const newSettingsData = [...prev];
      newSettingsData[index] = parseInt(e.target.value, 10);
      return newSettingsData;
    });

    if (sliderTimeout.current) {
      clearTimeout(sliderTimeout.current);
    }
    sliderTimeout.current = setTimeout(async () => {
      await apiPatch({
        path: '/bs/volume',
        req: {
          hCd: data[index].hCd,
          sCd: data[index].sCd,
          bsCd: data[index].bsCd,
          volume: Number(e.target.value),
        },
        contentType: 'application/json',
      });
    }, 500);
  };

  return (
    <Root>
      <div className='header flex-between'>
        <div role='button' tabIndex={0} onClick={handleClickCheckAll}>
          <CheckBoxCircle checked={checkAll} scale />
        </div>
        <div className='title'>
          {isTunnel && (
            <>
              {t('구역')}
              <span className='seperator' />
            </>
          )}
          {t('방송장비명')}
        </div>
        <div>{t('상태')}</div>
      </div>
      <div className='body'>
        {data.map((el: BroadcastList, index: number) => (
          <div className='list' key={index}>
            <div className={`content_wrapper ${state[index]?.connectValue === 1 ? 'selectable' : ''}`} tabIndex={0} role='button' onClick={() => handleClickCheckBox(index)}>
              <div className='content'>
                <CheckBoxCircle checked={(state[index]?.connectValue === 1 || state[index]?.connectValue === 2) && selectSpeaker.includes(index)} scale />
                <span className='index'>{index + 1}</span>
                <div className='text'>
                  {isTunnel && (
                    <>
                      {el.tadName}
                      <span className='seperator' />
                    </>
                  )}
                  {el.bsName}
                </div>
              </div>
              <div className={`badge ${getColor(state[index]?.connectValue)}`}>
                <span className='bullet' />
                <span className='bullet-status'>{getText(state[index]?.connectValue)}</span>
              </div>
            </div>
            <div className='slider-container'>
              <span className='material-symbols-rounded'>volume_up</span>
              <input
                type='range'
                min='1'
                max='100'
                step='1'
                value={settingsData[index] || 0}
                onChange={(e) => handleSliderValue(e, index)}
                disabled={state[index]?.connectValue === 3 ? !false : !data[index].volume}
              />
              <span className='volume-value'>{settingsData[index] || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </Root>
  );
};

export default BroadcastEquipList;
